import { z } from 'zod';

import {
    type V3ChatEntry,
    type V3MessageEntry,
} from '@apps/web/features/phoebe_chat_v3/model/transcript';
import { workingIntentTitle } from '@apps/web/features/phoebe_chat_v3/model/working_status';

const deltaBatchLineSchema = z.object({
    type: z.literal('delta_batch'),
    id: z.string().min(1),
    batch: z.object({
        run_id: z.string().min(1),
        model_pass_id: z.string().min(1),
        deltas: z.array(
            z.object({
                sequence: z.number().int().positive(),
                item_id: z.string().min(1),
                kind: z.enum(['text', 'reasoning']),
                content: z.string(),
            }),
        ),
        completions: z
            .array(
                z.object({
                    sequence: z.number().int().positive(),
                    kind: z.enum(['text', 'reasoning']),
                    item_ids: z.array(z.string().min(1)).min(1),
                    durable_item_id: z.string().min(1),
                }),
            )
            .default([]),
    }),
});

const doneLineSchema = z.object({
    type: z.literal('done'),
    id: z.string().min(1),
});

type LiveDeltaState = {
    readonly id: string;
    readonly kind: 'text' | 'reasoning';
    readonly content: string;
    readonly createdAt: string;
    readonly lastSequence: number;
    readonly sourceItemId: string | null;
};

export function filterCommittedLiveEntries(
    durableEntries: ReadonlyArray<V3ChatEntry>,
    liveEntries: ReadonlyArray<V3MessageEntry>,
    handoff?: {
        readonly isBusy: boolean;
        readonly baselineDurableAssistantItemIds: ReadonlySet<string>;
    },
): ReadonlyArray<V3MessageEntry> {
    const durableItemIds = new Set(
        durableEntries.flatMap((entry) =>
            entry.kind === 'message' && entry.sourceItemId !== null
                ? [entry.sourceItemId]
                : [],
        ),
    );
    const durableAnswerArrivedAfterTurnStarted =
        handoff !== undefined &&
        !handoff.isBusy &&
        durableEntries.some(
            (entry) =>
                entry.kind === 'message' &&
                entry.role === 'assistant' &&
                entry.sourceItemId !== null &&
                !handoff.baselineDurableAssistantItemIds.has(
                    entry.sourceItemId,
                ),
        );
    return liveEntries.filter((entry) => {
        if (
            entry.sourceItemId !== null &&
            durableItemIds.has(entry.sourceItemId)
        ) {
            return false;
        }
        return !durableAnswerArrivedAfterTurnStarted;
    });
}

export class V3LiveDeltaProjection {
    private readonly states = new Map<string, LiveDeltaState>();
    private latestReasoningKey: string | null = null;
    private cursor = '0-0';

    constructor(
        private readonly runId: string,
        initialCursor = '0-0',
    ) {
        this.cursor = initialCursor;
    }

    getCursor(): string {
        return this.cursor;
    }

    apply(value: unknown): boolean {
        const batchLine = deltaBatchLineSchema.safeParse(value);
        if (batchLine.success) {
            this.cursor = batchLine.data.id;
            if (batchLine.data.batch.run_id !== this.runId) {
                return false;
            }
            let changed = false;
            for (const delta of batchLine.data.batch.deltas) {
                const key = [
                    batchLine.data.batch.model_pass_id,
                    delta.kind,
                    delta.item_id,
                ].join(':');
                const current = this.states.get(key);
                if (
                    current !== undefined &&
                    delta.sequence <= current.lastSequence
                ) {
                    continue;
                }
                this.states.set(key, {
                    id: `live:${key}`,
                    kind: delta.kind,
                    content: `${current?.content ?? ''}${delta.content}`,
                    createdAt: current?.createdAt ?? new Date().toISOString(),
                    lastSequence: delta.sequence,
                    sourceItemId: current?.sourceItemId ?? null,
                });
                if (delta.kind === 'reasoning') {
                    this.latestReasoningKey = key;
                }
                changed = true;
            }
            for (const completion of batchLine.data.batch.completions) {
                for (const itemId of completion.item_ids) {
                    const key = [
                        batchLine.data.batch.model_pass_id,
                        completion.kind,
                        itemId,
                    ].join(':');
                    const current = this.states.get(key);
                    if (
                        current === undefined ||
                        completion.sequence <= current.lastSequence
                    ) {
                        continue;
                    }
                    this.states.set(key, {
                        ...current,
                        lastSequence: completion.sequence,
                        sourceItemId: completion.durable_item_id,
                    });
                    changed = true;
                }
            }
            return changed;
        }
        const doneLine = doneLineSchema.safeParse(value);
        if (doneLine.success) {
            this.cursor = doneLine.data.id;
        }
        return false;
    }

    latestReasoningTitle(): string | null {
        if (this.latestReasoningKey === null) {
            return null;
        }
        const reasoning = this.states.get(this.latestReasoningKey);
        if (reasoning === undefined || reasoning.kind !== 'reasoning') {
            return null;
        }
        return workingIntentTitle(reasoning.content);
    }

    toEntries(): ReadonlyArray<V3MessageEntry> {
        const entries: Array<V3MessageEntry> = [];
        for (const state of this.states.values()) {
            if (state.kind === 'reasoning') {
                continue;
            }
            entries.push({
                kind: 'message',
                id: state.id,
                createdAt: state.createdAt,
                role: 'assistant',
                content: state.content,
                author: null,
                isError: false,
                pendingDeliveryMode: null,
                sourceItemId: state.sourceItemId,
                revision: `${state.lastSequence}:${state.sourceItemId ?? ''}:${state.content}`,
            });
        }
        return entries;
    }
}
